import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import Spinner from '../components/Spinner.jsx';

export default function Karteikarten() {
  const [cards, setCards]     = useState([]);
  const [idx, setIdx]         = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [stats, setStats]     = useState({ easy: 0, good: 0, hard: 0 });
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    api.getQuiz({ type: 'flashcard', limit: 20 })
      .then((qs) => { setCards(qs); setLoadError(null); })
      .catch((e) => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const card = cards[idx];

  const rate = async (quality) => {
    setSaving(true);
    try {
      await api.submitAttempt({ question_id: card.id, chosen: [quality] });
      // Erst nach erfolgreichem Speichern zählen — sonst zählt ein Retry doppelt
      setStats((s) => ({ ...s, [quality]: s[quality] + 1 }));
      setSaveError(null);

      if (idx + 1 >= cards.length) {
        // Neue Runde laden — fällige Karten zuerst
        setIdx(0); setFlipped(false);
        setCards(await api.getQuiz({ type: 'flashcard', limit: 20 }));
        setLoadError(null);
      } else {
        setIdx((i) => i + 1);
        setFlipped(false);
      }
    } catch (e) {
      setSaveError(e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center gap-3">
        <Link to="/lernen" className="text-sm text-blue-600 hover:underline">← Zurück</Link>
        <span className="text-sm text-gray-500 ml-auto">
          {stats.easy + stats.good + stats.hard > 0 &&
            `Sitzung: ${stats.easy}× leicht · ${stats.good}× gut · ${stats.hard}× schwer`}
        </span>
      </div>

      {loading && <div className="flex justify-center py-12"><Spinner size="lg" /></div>}

      {loadError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          Karteikarten konnten nicht geladen werden: {loadError}
        </div>
      )}

      {saveError && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
          Bewertung konnte nicht gespeichert werden: {saveError} — bitte erneut bewerten.
        </div>
      )}

      {!loading && !loadError && cards.length === 0 && (
        <div className="card text-center py-10 text-gray-500 text-sm">
          Keine Karteikarten vorhanden.
        </div>
      )}

      {!loading && card && (
        <div className="space-y-4">
          {/* Fortschritt */}
          <div className="flex items-center gap-2">
            <div className="h-1.5 flex-1 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600" style={{ width: `${((idx+1)/cards.length)*100}%` }} />
            </div>
            <span className="text-xs text-gray-400 shrink-0">{idx+1}/{cards.length}</span>
          </div>

          {/* Karte */}
          <div
            className="card min-h-[200px] flex flex-col items-center justify-center text-center cursor-pointer select-none hover:shadow-md transition-shadow"
            onClick={() => setFlipped((f) => !f)}
          >
            {!flipped ? (
              <>
                <span className="text-xs uppercase tracking-wide text-blue-600 mb-3">Vorderseite — tippen zum Umdrehen</span>
                <p className="font-medium text-gray-900 text-lg leading-snug">{card.prompt}</p>
              </>
            ) : (
              <>
                <span className="text-xs uppercase tracking-wide text-green-600 mb-3">Rückseite</span>
                <p className="text-gray-800 leading-relaxed">{card.back ?? card.explanation}</p>
              </>
            )}
          </div>

          {flipped && (
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => rate('hard')} disabled={saving}
                className="btn py-3 bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 text-sm font-medium rounded-lg">
                😓 Schwer<br /><span className="text-xs font-normal">Bald wiederholen</span>
              </button>
              <button onClick={() => rate('good')} disabled={saving}
                className="btn py-3 bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 text-sm font-medium rounded-lg">
                🙂 Gut<br /><span className="text-xs font-normal">Normaler Rhythmus</span>
              </button>
              <button onClick={() => rate('easy')} disabled={saving}
                className="btn py-3 bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 text-sm font-medium rounded-lg">
                😄 Leicht<br /><span className="text-xs font-normal">Langer Abstand</span>
              </button>
            </div>
          )}

          {!flipped && (
            <button onClick={() => setFlipped(true)} className="btn-secondary w-full">
              Antwort zeigen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
